import { useState, useMemo, useEffect } from 'react';
import { X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useChatMessages, useApi, IThread } from '@chainlit/react-client';
import { useLayoutMaxWidth } from '@/hooks/useLayoutMaxWidth';

interface SessionExpiryBannerProps {
  sessionDays?: number;
  className?: string;
  show?: boolean; // Allow parent to control visibility
}

const SessionExpiryBanner = ({ 
  sessionDays = 30,
  className,
  show = true
}: SessionExpiryBannerProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const { threadId, messages } = useChatMessages();
  const layoutMaxWidth = useLayoutMaxWidth();
  
  const { data: thread, isLoading } = useApi<IThread>(
    threadId ? `/project/thread/${threadId}` : null,
    {
      revalidateOnFocus: false
    }
  );
  
  const expiryDate = useMemo(() => {
    if (thread?.createdAt) {
      const createdAt = new Date(thread.createdAt);
      return new Date(createdAt.getTime() + sessionDays * 24 * 60 * 60 * 1000);
    }
    // Fallback to 30 days from now if no thread data
    return new Date(Date.now() + sessionDays * 24 * 60 * 60 * 1000);
  }, [thread?.createdAt, sessionDays]);
  
  // Reset visibility when threadId changes
  useEffect(() => {
    setIsVisible(true);
  }, [threadId]);
  
  // Only show banner for existing chats (chats that have messages)
  const hasMessages = messages && messages.length > 0;
  
  // Don't show banner until thread data is loaded, we have a createdAt date, and the chat has messages
  if (!isVisible || !show || isLoading || !thread?.createdAt || !hasMessages) return null;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div
      className={cn(
        'relative w-full z-30',
        'py-3',
        // Extend to the right edge on desktop
        'md:-mr-20',
        className
      )}
      style={{
        background: 'linear-gradient(to right, rgb(239, 246, 255), rgb(238, 242, 255))',
        borderBottom: '1px solid rgba(191, 219, 254, 0.5)'
      }}
    >
      <div className="relative w-full">
        <div 
          className="flex items-start mx-auto w-full px-4 pr-12"
          style={{
            maxWidth: layoutMaxWidth
          }}
        >
          <div className="flex items-start gap-3">
            <Clock 
              className="w-5 h-5 flex-shrink-0 mt-0.5" 
              style={{ color: 'rgb(37, 99, 235)' }}
            />
            <div className="flex flex-col">
              <span 
                className="text-sm font-semibold"
                style={{ color: 'rgb(15, 23, 42)' }}
              >
                Conversations are saved for {sessionDays} days
              </span>
              <span 
                className="text-sm"
                style={{ color: 'rgb(71, 85, 105)' }}
              >
                This chat will expire on {formatDate(expiryDate)}.
              </span>
            </div>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsVisible(false)}
          className="absolute top-1/2 right-4 -translate-y-1/2 h-8 w-8 p-0 flex-shrink-0 hover:bg-gray-100"
          style={{ color: 'rgb(100, 116, 139)' }}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default SessionExpiryBanner;